/**
 * PostHog destination for the analytics bus.
 *
 * Wraps the existing posthog-js (web) and @capawesome/capacitor-posthog
 * (native) integrations so the rest of the app talks only to the bus.
 */
import posthogJs from 'posthog-js';
import { Posthog as PosthogNative } from '@capawesome/capacitor-posthog';
import { Capacitor } from '@capacitor/core';
import type { AnalyticsDestination, EventName, EventProps, SuperProperties } from '../types';
import { analyticsAllowed } from '../../cookie-prefs';

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

let ready = false;
let currentScreenName: string | null = null;
let currentScreenProps: EventProps | undefined;
let superProps: SuperProperties = {};

const log = (...args: unknown[]) => console.log('[Analytics:PostHog]', ...args);

const isPlaceholder = !POSTHOG_KEY || (POSTHOG_KEY as string).includes('your_posthog_project_api_key');
const isNative = Capacitor.isNativePlatform();

const enrich = (props?: EventProps): EventProps => {
  const enriched: EventProps = { ...superProps, ...(props || {}) };
  try {
    if (typeof window !== 'undefined' && window.location) {
      enriched.$current_url = enriched.$current_url ?? window.location.href;
      enriched.$pathname = enriched.$pathname ?? window.location.pathname;
      enriched.$host = enriched.$host ?? window.location.host;
    }
  } catch { /* noop */ }
  if (currentScreenName) {
    enriched.$screen_name = enriched.$screen_name ?? currentScreenName;
    if (!enriched.$pathname) enriched.$pathname = currentScreenName;
  }
  if (currentScreenProps) {
    for (const [k, v] of Object.entries(currentScreenProps)) {
      if (enriched[k] === undefined) enriched[k] = v;
    }
  }
  return enriched;
};

export const posthogDestination: AnalyticsDestination = {
  name: 'posthog',

  async init() {

    if (isPlaceholder) {
      log('skipped (missing key)');
      return;
    }
    if (!analyticsAllowed()) {
      log('skipped (no consent)');
      return;
    }
    if (ready) return;

    if (isNative) {
      try {
        await PosthogNative.setup({
          apiKey: POSTHOG_KEY,
          host: POSTHOG_HOST,
          enableSessionReplay: true,
        });
        ready = true;
        log('native ready');
      } catch (err) {
        console.error('[Analytics:PostHog] native init failed', err);
      }
    } else {
      try {
        posthogJs.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          autocapture: true,
          capture_pageview: true,
          capture_performance: true,
          persistence: 'localStorage',
          enable_recording_console_log: true,
          // @ts-ignore
          enable_exception_autocapture: true,
        });
        ready = true;
        log('web ready');
      } catch (err) {
        console.error('[Analytics:PostHog] web init failed', err);
      }
    }
  },

  isReady() {
    return ready;
  },

  identify(distinctId, traits) {
    if (!ready) return;
    if (isNative) {
      PosthogNative.identify({ distinctId, userProperties: traits as any });
    } else {
      posthogJs.identify(distinctId, traits as any);
    }
  },

  // Attaches properties (e.g. the email typed on the auth screen) to the
  // current profile — including anonymous ones, so abandoned signups stop
  // showing up in PostHog as a bare UUID with no email.
  setPersonProperties(props) {
    if (!ready) return;
    if (isNative) {
      PosthogNative.capture({ event: '$set', properties: { $set: props } as any });
    } else {
      posthogJs.setPersonProperties(props as any);
    }
  },

  reset() {
    if (!ready) return;
    if (isNative) PosthogNative.reset();
    else posthogJs.reset();
  },

  track(event: EventName | string, props: EventProps) {
    if (!ready) return;
    const merged = enrich(props);
    if (isNative) {
      PosthogNative.capture({ event, properties: merged as any });
    } else {
      posthogJs.capture(event, merged as any);
    }
  },

  trackScreen(name: string, props: EventProps) {
    currentScreenName = name;
    currentScreenProps = props;
    if (!ready) return;
    if (isNative) {
      PosthogNative.screen({ screenTitle: name, properties: enrich(props) as any });
    } else {
      posthogJs.capture('$pageview', enrich({ $pathname: name, ...props }) as any);
    }
  },

  setSuperProperties(props: SuperProperties) {
    superProps = { ...superProps, ...props };
    if (!ready) return;
    // PostHog web supports register(); native plugin doesn't, so we always merge in enrich()
    if (!isNative) {
      try { posthogJs.register(superProps as any); } catch { /* noop */ }
    }
  },
};

/**
 * React to cookie-consent changes at runtime.
 *
 * Previously PostHog only checked consent once, during boot: users who accepted
 * the banner after load stayed untracked until a full reload, so their signup
 * events were lost. Now granting consent initializes immediately (and the bus
 * drains its buffer), while revoking stops sending and clears identity.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('careos:cookie-prefs-changed', () => {
    if (analyticsAllowed()) {
      if (!ready) void posthogDestination.init();
    } else if (ready) {
      try { posthogDestination.reset(); } catch { /* noop */ }
      ready = false;
      log('disabled (consent revoked)');
    }
  });
}

